// package com.rajen.angrejidictionary.components.widgets
package com.rajen.angrejidictionary.components.widgets

import androidx.compose.runtime.*
import com.rajen.angrejidictionary.analytics.Umami
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.disabled
import com.varabyte.kobweb.compose.css.textAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.await
import kotlinx.coroutines.launch
import kotlinx.serialization.json.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Response

enum class FormStatus { Idle, Loading, Success, Error }

@Composable
fun ContactForm() {
    var name by remember { mutableStateOf("") }
    var email by remember { mutableStateOf("") }
    var subject by remember { mutableStateOf("") }
    var message by remember { mutableStateOf("") }

    var status by remember { mutableStateOf(FormStatus.Idle) }
    var errorMessage by remember { mutableStateOf("") }
    var hasAttemptedSubmit by remember { mutableStateOf(false) }

    val isSubmitting = status == FormStatus.Loading

    // Validation Logic
    val isNameValid = name.isNotBlank()
    val isEmailValid = email.isNotBlank() && email.contains("@") && email.contains(".")
    val isSubjectValid = subject.isNotBlank()
    val isMessageValid = message.isNotBlank()
    val isFormValid = isNameValid && isEmailValid && isSubjectValid && isMessageValid && !isSubmitting

    val nameError = when {
        !isNameValid && hasAttemptedSubmit -> "Name is required"
        name.isNotEmpty() && name.isBlank() -> "Name cannot be just spaces"
        else -> null
    }
    val emailError = when {
        !isEmailValid && hasAttemptedSubmit -> if (email.isBlank()) "Email is required" else "Please enter a valid email"
        email.isNotEmpty() && (!email.contains("@") || !email.contains(".")) -> "Please enter a valid email"
        else -> null
    }
    val subjectError = when {
        !isSubjectValid && hasAttemptedSubmit -> "Subject is required"
        subject.isNotEmpty() && subject.isBlank() -> "Subject cannot be just spaces"
        else -> null
    }
    val messageError = when {
        !isMessageValid && hasAttemptedSubmit -> "Message is required"
        message.isNotEmpty() && message.isBlank() -> "Message cannot be just spaces"
        else -> null
    }

    fun resetForm() {
        name = ""; email = ""; subject = ""; message = ""
        status = FormStatus.Idle; errorMessage = ""; hasAttemptedSubmit = false
    }

    fun submitForm() {
        hasAttemptedSubmit = true
        if (!isFormValid) return

        Umami.trackContactSubmit()
        status = FormStatus.Loading

        MainScope().launch {
            try {
                // Simple JSON string construction to avoid extra imports
                val payload = """
                    {
                        "name": "${name.replace("\"", "\\\"")}",
                        "email": "${email.replace("\"", "\\\"")}",
                        "subject": "${subject.replace("\"", "\\\"")}",
                        "message": "${message.replace("\"", "\\\"").replace("\n", "\\n")}"
                    }
                """.trimIndent()

                val init = RequestInit(
                    method = "POST",
                    headers = js("({'Content-Type': 'application/json'})"),
                    body = payload
                )

                val response: Response = window.fetch("https://api.angrejidictionary.com/api/v1/contact", init).await()
                val responseText = response.text().await()

                if (response.ok) {
                    status = FormStatus.Success
                    Umami.trackContactSuccess()
                } else {
                    try {
                        val json = Json.parseToJsonElement(responseText).jsonObject
                        val error = json["error"]?.jsonObject
                        errorMessage = error?.get("message")?.jsonPrimitive?.content ?: "Failed to send message."
                    } catch (_: Exception) {
                        errorMessage = "Failed to send message. Please try again."
                    }
                    status = FormStatus.Error
                    Umami.trackContactError(errorMessage)
                }
            } catch (_: Exception) {
                errorMessage = "Network error. Please check your connection."
                status = FormStatus.Error
                Umami.trackContactError("network-error")
            }
        }
    }

    // UI Rendering
    Column(
        Modifier.fillMaxWidth().maxWidth(650.px),
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.spacedBy(1.25.cssRem)
    ) {
        if (status == FormStatus.Success) {
            Div(attrs = {
                style {
                    width(100.percent); padding(24.px); backgroundColor(rgb(235, 249, 240))
                    color(rgb(22, 128, 68)); borderRadius(14.px); textAlign(TextAlign.Center)
                    fontWeight(600); fontSize(16.px)
                }
            }) {
                Text("✅ Message sent successfully! We'll get back to you soon.")
                Div(attrs = { style { marginTop(16.px) } }) {
                    Button(attrs = {
                        style {
                            backgroundColor(rgb(211, 53, 62)); color(rgb(255, 255, 255))
                            padding(10.px, 20.px); borderRadius(10.px); border(0.px)
                            fontSize(14.px); fontWeight("600"); cursor("pointer")
                        }
                        onClick { resetForm() }
                    }) { Text("Send another message") }
                }
            }
        } else {
            ContactInput("Your Name", "name", InputType.Text, name, { name = it }, isSubmitting, nameError)
            ContactInput("Email Address", "email", InputType.Email, email, { email = it }, isSubmitting, emailError)
            ContactInput("Subject", "subject", InputType.Text, subject, { subject = it }, isSubmitting, subjectError)

            // Message TextArea
            Div(attrs = { style { width(100.percent); maxWidth(500.px) } }) {
                Label(attrs = { style { fontSize(15.px); fontWeight(600); color(rgb(33, 0, 0)) } }) { Text("Message") }
                TextArea(attrs = {
                    value(message); onInput { message = it.value }; disabled(isSubmitting); rows(6)
                    style {
                        width(100.percent); padding(14.px); backgroundColor(rgb(255, 255, 255))
                        color(rgb(33, 0, 0)); property("caret-color", "#210000")
                        border(
                            1.px,
                            LineStyle.Solid,
                            if (messageError != null) rgb(211, 53, 62) else rgb(230, 210, 200)
                        )
                        borderRadius(14.px); fontSize(16.px); property("box-sizing", "border-box")
                        property("resize", "vertical"); property("outline", "none")
                        if (isSubmitting) property("opacity", "0.6")
                    }
                })
                if (messageError != null) {
                    SpanText(
                        messageError,
                        Modifier
                            .color(Color.rgb(211, 53, 62))
                            .fontSize(12.px)
                            .margin(top = 4.px)
                    )
                }
            }

            if (status == FormStatus.Error && errorMessage.isNotBlank()) {
                Div(attrs = {
                    style {
                        width(100.percent); maxWidth(500.px); padding(14.px)
                        backgroundColor(rgb(253, 237, 237)); color(rgb(211, 53, 62))
                        borderRadius(10.px); textAlign(TextAlign.Center); fontSize(14.px); fontWeight(500)
                    }
                }) { Text("❌ $errorMessage") }
            }

            Button(attrs = {
                disabled(isSubmitting)
                style {
                    backgroundColor(if (isFormValid) rgb(211, 53, 62) else rgba(211, 53, 62, 0.6f))
                    color(rgb(255, 255, 255)); padding(16.px, 32.px); borderRadius(16.px); border(0.px)
                    fontSize(18.px); fontWeight("700"); cursor(if (isFormValid) "pointer" else "not-allowed")
                    property("box-shadow", "0 8px 20px rgba(211,53,62,.25)"); property("transition", "all .2s ease")
                    if (isSubmitting) property("opacity", "0.7")
                }
                onClick { submitForm() }
            }) { Text(if (isSubmitting) "Sending..." else "Send Message") }
        }
    }
}